/* SIZESOFTX
=========================================================================================
* Descripción: Administración de usuarios de la tabla Accesos de TenantSizeSoftX (solo SA)
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 11/09/2026 | Lic. José Luis Aragón Cervantes  | Creación
* ---------------------------------------------------------------------------------------
*/

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import {
  apiRolesList,
  apiUsuarioActualizar,
  apiUsuarioCrear,
  apiUsuariosList,
  apiUsuarioRestablecerPassword,
} from "../settings/supportApi";
import {
  EMAIL_REGEX,
  sanitizarEmail,
  sanitizarTexto,
  validarSinInyeccion,
} from "../settings/inputSecurity";

const emptyForm = {
  usuario: "",
  nombre: "",
  email: "",
  password: "",
  roles: [],
};

const emptyEditForm = {
  id: null,
  usuario: "",
  nombre: "",
  email: "",
  activo: true,
  roles: [],
};

const UsuariosPage = () => {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [open, setOpen] = useState(false);
  const [passOpen, setPassOpen] = useState(false);
  const [passUserId, setPassUserId] = useState(null);
  const [passValue, setPassValue] = useState("");

  const [form, setForm] = useState(emptyForm);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyEditForm);

  const cargar = useCallback(() => {
    setLoading(true);
    Promise.all([apiUsuariosList(), apiRolesList()])
      .then(([u, r]) => {
        setRows(u || []);
        setRoles(r || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setEdit = (k) => (e) => setEditForm({ ...editForm, [k]: e.target.value });

  const toggleRol = (nombre) => {
    const actual = form.roles.includes(nombre);
    setForm({ ...form, roles: actual ? form.roles.filter((r) => r !== nombre) : [...form.roles, nombre] });
  };

  const toggleEditRol = (nombre) => {
    const actual = editForm.roles.includes(nombre);
    setEditForm({
      ...editForm,
      roles: actual ? editForm.roles.filter((r) => r !== nombre) : [...editForm.roles, nombre],
    });
  };

  const abrirEdicion = (row) => {
    setError("");
    setOk("");
    setEditForm({
      id: row.id,
      usuario: row.usuario || "",
      nombre: row.nombre || "",
      email: row.email || "",
      activo: row.activo !== false,
      roles: Array.isArray(row.roles) ? row.roles : [],
    });
    setEditOpen(true);
  };

  const crear = async () => {
    setError("");
    setOk("");
    try {
      const usuario = sanitizarTexto(form.usuario, 25).replace(/\s+/g, "");
      const nombre = sanitizarTexto(form.nombre, 70);
      const email = sanitizarEmail(form.email, 100);
      const password = String(form.password || "").slice(0, 25);
      if (!usuario) {
        setError("Usuario (login) requerido.");
        return;
      }
      if (!nombre) {
        setError("Nombre requerido, máximo 70 caracteres.");
        return;
      }
      if (!email || !EMAIL_REGEX.test(email)) {
        setError("Email requerido, válido y máximo 100 caracteres.");
        return;
      }
      if (!password || password.length < 8) {
        setError("Password requerido de 8 a 25 caracteres.");
        return;
      }
      if (!form.roles.length) {
        setError("Debe asignar al menos un rol.");
        return;
      }
      const bloqueo = validarSinInyeccion([
        { etiqueta: "Usuario", valor: form.usuario },
        { etiqueta: "Nombre", valor: form.nombre },
        { etiqueta: "Email", valor: form.email },
        { etiqueta: "Password", valor: form.password },
      ]);
      if (bloqueo) {
        setError(`Bloqueado por seguridad: ${bloqueo}`);
        return;
      }
      await apiUsuarioCrear({ usuario, nombre, email, password, roles: form.roles });
      setOk("Usuario creado.");
      setForm(emptyForm);
      setOpen(false);
      await cargar();
    } catch (err) {
      setError(err.message);
    }
  };

  const guardarEdicion = async () => {
    setError("");
    setOk("");
    try {
      const nombre = sanitizarTexto(editForm.nombre, 70);
      const email = sanitizarEmail(editForm.email, 100);
      if (!nombre || nombre.length > 70) {
        setError("Nombre requerido, máximo 70 caracteres.");
        return;
      }
      if (!email || !EMAIL_REGEX.test(email) || email.length > 100) {
        setError("Email requerido, válido y máximo 100 caracteres.");
        return;
      }
      if (!editForm.roles.length) {
        setError("Debe asignar al menos un rol.");
        return;
      }
      const bloqueo = validarSinInyeccion([
        { etiqueta: "Nombre", valor: editForm.nombre },
        { etiqueta: "Email", valor: editForm.email },
      ]);
      if (bloqueo) {
        setError(`Bloqueado por seguridad: ${bloqueo}`);
        return;
      }

      await apiUsuarioActualizar({
        id: editForm.id,
        nombre,
        email,
        activo: Boolean(editForm.activo),
        roles: editForm.roles,
      });
      setOk("Usuario actualizado.");
      setEditOpen(false);
      setEditForm(emptyEditForm);
      await cargar();
    } catch (err) {
      setError(err.message);
    }
  };

  const restablecerPassword = async () => {
    setError("");
    setOk("");
    try {
      const nuevo = String(passValue || "").slice(0, 25);
      if (!nuevo || nuevo.length < 8) {
        setError("Password requerido de 8 a 25 caracteres.");
        return;
      }
      const bloqueo = validarSinInyeccion([{ etiqueta: "Password", valor: nuevo }]);
      if (bloqueo) {
        setError(`Bloqueado por seguridad: ${bloqueo}`);
        return;
      }
      await apiUsuarioRestablecerPassword(passUserId, nuevo);
      setOk("Password restablecido.");
      setPassOpen(false);
      setPassValue("");
    } catch (err) {
      setError(err.message);
    }
  };

  const columns = [
    { field: "usuario", headerName: "Usuario", width: 140 },
    { field: "nombre", headerName: "Nombre", width: 180 },
    { field: "email", headerName: "Email", width: 220 },
    {
      field: "activo",
      headerName: "Estado",
      width: 120,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.value === false ? "Inactivo" : "Activo"}
          color={p.value === false ? "warning" : "success"}
          variant="outlined"
        />
      ),
    },
    {
      field: "roles",
      headerName: "Roles",
      width: 200,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          {(p.value || []).map((r) => (
            <Chip key={r} size="small" label={r} color={r === "SA" ? "primary" : "default"} />
          ))}
        </Stack>
      ),
    },
    {
      field: "acciones",
      headerName: "Acciones",
      width: 260,
      sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={() => abrirEdicion(p.row)}>
            Editar
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setPassUserId(p.row.id);
              setPassOpen(true);
            }}
          >
            Password
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Usuarios de Accesos
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Administra la tabla Accesos de TenantSizeSoftX (misma que usa Seguridad). Los roles del
        campo Roles definen la jerarquía: SA (administrador), SOPORTE/TECNICO (técnico), LECTURA.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {ok && (
        <Alert severity="success" onClose={() => setOk("")} sx={{ mb: 2 }}>
          {ok}
        </Alert>
      )}

      <Button variant="contained" sx={{ mb: 2 }} onClick={() => setOpen(true)}>
        Nuevo usuario
      </Button>

      <Box sx={{ height: 520 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        />
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo usuario en Accesos</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Usuario (login)"
              value={form.usuario}
              onChange={set("usuario")}
              inputProps={{ maxLength: 25 }}
              fullWidth
            />
            <TextField
              label="Nombre"
              value={form.nombre}
              onChange={set("nombre")}
              inputProps={{ maxLength: 70 }}
              fullWidth
            />
            <TextField
              label="Email"
              value={form.email}
              onChange={set("email")}
              inputProps={{ maxLength: 100 }}
              fullWidth
            />
            <TextField
              label="Password (8 a 25 caracteres)"
              type="password"
              value={form.password}
              onChange={set("password")}
              inputProps={{ maxLength: 25 }}
              fullWidth
            />
            <Typography variant="body2">Roles:</Typography>
            <Stack direction="row" spacing={2}>
              {roles.map((r) => (
                <FormControlLabel
                  key={r.id}
                  control={
                    <Checkbox checked={form.roles.includes(r.nombre)} onChange={() => toggleRol(r.nombre)} />
                  }
                  label={r.nombre}
                />
              ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={crear}>
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar usuario en Accesos</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Usuario (login)" value={editForm.usuario} disabled fullWidth />
            <TextField
              label="Nombre"
              value={editForm.nombre}
              onChange={setEdit("nombre")}
              inputProps={{ maxLength: 70 }}
              fullWidth
            />
            <TextField
              label="Email"
              value={editForm.email}
              onChange={setEdit("email")}
              inputProps={{ maxLength: 100 }}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(editForm.activo)}
                  onChange={(e) => setEditForm({ ...editForm, activo: e.target.checked })}
                />
              }
              label={editForm.activo ? "Activo" : "Inactivo"}
            />
            <Typography variant="body2">Roles:</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {roles.map((r) => (
                <FormControlLabel
                  key={r.id}
                  control={
                    <Checkbox checked={editForm.roles.includes(r.nombre)} onChange={() => toggleEditRol(r.nombre)} />
                  }
                  label={r.nombre}
                />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              El password se cambia con la acción Password para mantener separado el restablecimiento.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarEdicion}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={passOpen} onClose={() => setPassOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Restablecer password</DialogTitle>
        <DialogContent>
          <TextField
            label="Nuevo password (8 a 25 caracteres)"
            type="password"
            value={passValue}
            onChange={(e) => setPassValue(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPassOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={restablecerPassword}>
            Restablecer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsuariosPage;
